import { useState, useEffect } from "react";
import {
  Header,
  MediaQuery,
  Flex,
  Button,
  Box,
  createStyles,
} from "@mantine/core";
import { Link, useLocation } from "react-router-dom";
import Dropdown from "./Dropdown";
import { IconPhoneCall } from "@tabler/icons-react";
import { responsive_header_height } from "../constant/Responsive";
import PageContainer from "./layout/PageContainer";

const useStyles = createStyles((theme) => ({
  header: {
    position: "sticky",
    top: 0,
    zIndex: 100,
    backgroundColor: "rgba(252, 251, 249, 0.97)",
    backdropFilter: "blur(16px)",
    borderBottom: `1px solid ${theme.colors.gray[2]}`,
  },
  desktopGrid: {
    display: "grid",
    gridTemplateColumns: "minmax(120px, auto) 1fr minmax(120px, auto)",
    alignItems: "center",
    width: "100%",
    height: "100%",
    columnGap: theme.spacing.md,
  },
  navRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexWrap: "wrap",
    gap: theme.spacing.lg,
  },
  navLink: {
    textDecoration: "none",
    color: theme.colors.gray[6],
    fontWeight: 500,
    fontSize: theme.fontSizes.sm,
    letterSpacing: "0.06em",
    transition: "color 0.2s ease",
    whiteSpace: "nowrap",
    padding: "4px 0",
    borderBottom: "2px solid transparent",
    "&:hover": {
      color: theme.colors.gray[8],
    },
  },
  navLinkActive: {
    color: theme.colors.gray[8],
    fontWeight: 600,
    borderBottomColor: theme.colors.orange[6],
  },
  brandWrap: {
    display: "inline-flex",
    alignItems: "center",
    textDecoration: "none",
  },
  logoImg: {
    height: 44,
    width: "auto",
    objectFit: "contain",
    display: "block",
    [theme.fn.smallerThan("sm")]: { height: 38 },
  },
  ctaCol: {
    display: "flex",
    justifyContent: "flex-end",
  },
  mobileShell: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    height: "100%",
  },
}));

const navItems = [
  { to: "/home", label: "首頁", match: (path, category) => path === "/" || path === "/home" },
  {
    to: "/product?category=fresh",
    label: "鮮果",
    match: (path, category) =>
      category === "fresh" || path.includes("mskfruit") || path.includes("d200fruit"),
  },
  {
    to: "/product?category=frozen",
    label: "急凍產品",
    match: (path, category) =>
      category === "frozen" ||
      category === "puree" ||
      path.includes("fruit400") ||
      path.includes("fruit500") ||
      path.includes("puree") ||
      path.includes("durianmochi"),
  },
  {
    to: "/product",
    label: "全部產品",
    match: (path, category) => (path === "/product" || path === "/retail") && !category,
  },
  { to: "/contact", label: "查詢訂銷", match: (path) => path === "/contact" },
];

function Logo() {
  const { classes } = useStyles();
  return (
    <Link to="/" className={classes.brandWrap}>
      <img
        src={`${process.env.PUBLIC_URL || ""}/companylogo2.png`}
        alt="猫山王朝"
        className={classes.logoImg}
        width={360}
        height={103}
        decoding="async"
      />
    </Link>
  );
}

export default function PageHeader() {
  const { classes, cx } = useStyles();
  const location = useLocation();
  const path = location.pathname;
  const category = new URLSearchParams(location.search).get("category");

  const getActiveIndex = () => navItems.findIndex((item) => item.match(path, category));

  const [navActive, setNavActive] = useState(getActiveIndex());

  useEffect(() => {
    setNavActive(getActiveIndex());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, location.search]);

  return (
    <Header className={classes.header} h={responsive_header_height} p={0}>
      <PageContainer h="100%">
        <MediaQuery smallerThan="md" styles={{ display: "none" }}>
          <div className={classes.desktopGrid}>
            <Logo />
            <nav className={classes.navRow} aria-label="主要導航">
              {navItems.map((item, i) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className={cx(classes.navLink, navActive === i && classes.navLinkActive)}
                  onMouseEnter={() => setNavActive(i)}
                  onMouseLeave={() => setNavActive(getActiveIndex())}
                >
                  {item.label}
                </Link>
              ))}
            </nav>
            <div className={classes.ctaCol}>
              <Link to="/contact" style={{ textDecoration: "none" }}>
                <Button
                  size="xs"
                  variant="filled"
                  color="orange"
                  radius="xl"
                  leftIcon={<IconPhoneCall size={14} />}
                >
                  9831 2915
                </Button>
              </Link>
            </div>
          </div>
        </MediaQuery>

        <MediaQuery largerThan="md" styles={{ display: "none" }}>
          <Flex className={classes.mobileShell}>
            <Logo />
            <Box>
              <Dropdown />
            </Box>
          </Flex>
        </MediaQuery>
      </PageContainer>
    </Header>
  );
}
